import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from './AdminLayout';
import { submissionsAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Eye, CheckCircle, FileText, Loader2 } from 'lucide-react';
import { formatDateShort } from '../../lib/utils';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  reviewed: 'bg-blue-100 text-blue-800',
  converted: 'bg-green-100 text-green-800',
};

const typeLabels = {
  story_pitch: 'Story Pitch',
  full_article: 'Full Article',
  photo_set: 'Photo Set',
  video: 'Video',
  tip: 'Tip',
};

export const AdminSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const res = await submissionsAPI.getAll();
      setSubmissions(res.data.submissions);
    } catch (error) {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkReviewed = async (id) => {
    setProcessing(true);
    try {
      await submissionsAPI.markReviewed(id);
      toast.success('Marked as reviewed');
      fetchSubmissions();
      setSelectedSubmission(null);
    } catch (error) {
      toast.error('Failed to update submission');
    } finally {
      setProcessing(false);
    }
  };

  const handleConvert = async (id) => {
    setProcessing(true);
    try {
      const res = await submissionsAPI.convert(id);
      toast.success('Converted to draft article');
      fetchSubmissions();
      setSelectedSubmission(null);
      // Navigate to edit the new article
      navigate(`/staff/articles/${res.data.article_id}`);
    } catch (error) {
      toast.error('Failed to convert submission');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-submissions-page">
        {/* Header */}
        <div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold">Submissions</h1>
          <p className="text-muted-foreground">Review and manage user submissions</p>
        </div>

        {/* Table */}
        <div className="border border-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead className="hidden md:table-cell">Submitter</TableHead>
                <TableHead className="hidden md:table-cell">Type</TableHead>
                <TableHead className="hidden md:table-cell">Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden md:table-cell">Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                    <TableCell className="hidden md:table-cell"><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell className="hidden md:table-cell"><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell className="hidden md:table-cell"><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                    <TableCell className="hidden md:table-cell"><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-8 w-20 ml-auto" /></TableCell>
                  </TableRow>
                ))
              ) : submissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                    No submissions yet
                  </TableCell>
                </TableRow>
              ) : (
                submissions.map((submission) => (
                  <TableRow key={submission.id} data-testid={`submission-row-${submission.id}`}>
                    <TableCell>
                      <div className="font-medium line-clamp-1">{submission.title}</div>
                      <div className="text-sm text-muted-foreground md:hidden">
                        {submission.name}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{submission.name}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      {typeLabels[submission.submission_type] || submission.submission_type}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{submission.category}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[submission.status]}>
                        {submission.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {formatDateShort(submission.created_at)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSelectedSubmission(submission)}
                        data-testid={`view-submission-${submission.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Submission Detail Dialog */}
        <Dialog open={!!selectedSubmission} onOpenChange={() => setSelectedSubmission(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                {selectedSubmission?.title}
              </DialogTitle>
            </DialogHeader>
            
            {selectedSubmission && (
              <div className="space-y-6" data-testid="submission-detail">
                {/* Meta */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Submitter:</span>
                    <p className="font-medium">{selectedSubmission.name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Email:</span>
                    <p className="font-medium">{selectedSubmission.email}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Type:</span>
                    <p className="font-medium">
                      {typeLabels[selectedSubmission.submission_type]}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Category:</span>
                    <p className="font-medium">{selectedSubmission.category}</p>
                  </div>
                  {selectedSubmission.phone && (
                    <div>
                      <span className="text-muted-foreground">Phone:</span>
                      <p className="font-medium">{selectedSubmission.phone}</p>
                    </div>
                  )}
                  {selectedSubmission.social_handles && (
                    <div>
                      <span className="text-muted-foreground">Social:</span>
                      <p className="font-medium">{selectedSubmission.social_handles}</p>
                    </div>
                  )}
                </div>

                {/* Summary */}
                <div>
                  <h4 className="font-medium mb-2">Summary</h4>
                  <p className="text-muted-foreground">{selectedSubmission.summary}</p>
                </div>

                {/* Content */}
                {selectedSubmission.content && (
                  <div>
                    <h4 className="font-medium mb-2">Full Content</h4>
                    <div className="bg-secondary/30 p-4 rounded-sm text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">
                      {selectedSubmission.content}
                    </div>
                  </div>
                )}

                {/* Video Link */}
                {selectedSubmission.video_link && (
                  <div>
                    <h4 className="font-medium mb-2">Video Link</h4>
                    <a 
                      href={selectedSubmission.video_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary underline"
                    >
                      {selectedSubmission.video_link}
                    </a>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t border-border">
                  {selectedSubmission.status === 'pending' && (
                    <Button
                      variant="outline"
                      onClick={() => handleMarkReviewed(selectedSubmission.id)}
                      disabled={processing}
                      data-testid="mark-reviewed-btn"
                    >
                      {processing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      )}
                      Mark Reviewed
                    </Button>
                  )}
                  {selectedSubmission.status !== 'converted' && (
                    <Button
                      onClick={() => handleConvert(selectedSubmission.id)}
                      disabled={processing}
                      data-testid="convert-to-article-btn"
                    >
                      {processing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <FileText className="h-4 w-4 mr-2" />
                      )}
                      Convert to Article
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminSubmissions;
